import ToastAlert from 'components/ToastAlert/ToastAlert'
import {
  ACCOUNT_EXECUTIVE_MAX_DRAFT_COMPANIES,
  CompanyStatus,
  HTTP_RESPONSE_STATUS_CODE,
  TOAST_PROPS_DEFAULT,
  TOTAL_COMPANY_STEPS_REGISTER
} from 'utils/constants'
import { AxiosError, AxiosResponse } from 'axios'
import { composeValidators } from './validations'
import { ICompany } from 'components/Company/interface'
import { ICONS_BY_COMPANY_TYPE } from 'components/Navigation/constants'
import { IFormProps, TGroupCompanies } from './interface'
import { isCompletedCompany } from './resourcesApi/helpers'
import { IToastAlert, ToastAlertType } from 'components/ToastAlert/interface'
import { TFunction } from 'i18next'
import { TUserTypes } from './resourcesApi/interface'
import { USER_TYPES } from './resourcesApi/constants'

interface IAxiosRequestHandler {
  onEnd?: () => void
  onError?: (error: AxiosError) => void
  onLoading?: (loading: boolean) => void
  onStart?: () => void
  onSuccess?: (response: AxiosResponse) => void
  onWarning?: (response: AxiosResponse) => void
  request: Promise<AxiosResponse>
  successStatus?: number
  t: TFunction
}

export const processErrorByStatusCode = (error: any, t: TFunction, apiError: any): boolean => {
  if (!apiError || Object.keys(apiError).length === 0) {
    return false
  }

  // gateway timeout
  if (error.response?.status === HTTP_RESPONSE_STATUS_CODE['504']) {
    ToastAlert({
      text: t(`customers.request.error.timeoutAlert.text`),
      title: t(`customers.request.error.timeoutAlert.title`),
      type: 'error',
      useTKey: true
    })
  }

  return true
}

export const showToastAlert = (t: TFunction, type?: ToastAlertType, tRoot?: string, className?: string): void => {
  const type_aux = type || 'error'
  const text_default = t(`toastAlert.${type_aux}.text`)
  const title_default = t(`toastAlert.${type_aux}.title`)

  ToastAlert({
    className: className || '',
    text: tRoot ? t(`${tRoot}.${type_aux}.text`, text_default) : text_default,
    title: tRoot ? t(`${tRoot}.${type_aux}.title`, title_default) : title_default,
    type: type_aux
  })
}

export const cleanHtmlForReference = (reference: React.RefObject<HTMLDivElement> | null): void => {
  if (reference?.current) {
    reference.current.innerHTML = ''
  }
}

export const setHtmlForReference = (reference: React.RefObject<HTMLDivElement> | null, htmlString: string): void => {
  if (reference?.current) {
    const parser = new DOMParser()
    const html_document = parser.parseFromString(htmlString, 'text/html')
    const html_body = html_document.getElementsByTagName('body')[0]
    const html_content = html_body?.innerHTML ? html_body.innerHTML : ''

    reference.current.innerHTML += html_content
  }
}

export const isAccountExecutiveUser = (userType: TUserTypes): boolean => {
  return userType === USER_TYPES.adOpsAccountExecutive
}

export const accountExecutiveExceedsMaxDraftCompanies = (
  userType: TUserTypes,
  inProcessCompanies: ICompany[]
): boolean => {
  return (
    userType === USER_TYPES.adOpsAccountExecutive && inProcessCompanies.length >= ACCOUNT_EXECUTIVE_MAX_DRAFT_COMPANIES
  )
}

export const getCompanyStatus = (company: ICompany): CompanyStatus => {
  const { isTest, stages, status, typeOrigin } = company

  switch (true) {
    case isTest:
      return CompanyStatus.TESTING
    case isCompletedCompany(company):
      return CompanyStatus.ACTIVE
    case status === CompanyStatus.ACTIVE &&
      stages.length <= TOTAL_COMPANY_STEPS_REGISTER &&
      typeOrigin !== 'SalesForce':
      return CompanyStatus.IN_PROCESS
    default:
      return CompanyStatus.INACTIVE
  }
}

export const groupCompaniesByType = (companies: ICompany[]): TGroupCompanies =>
  companies.reduce(
    (companyGroups: TGroupCompanies, currentCompany: ICompany) => {
      const CURRENT_COMPANY_STATUS: CompanyStatus = getCompanyStatus(currentCompany)
      if (CURRENT_COMPANY_STATUS && !!currentCompany.idReferenceCompany && !currentCompany.idParentCompany) {
        companyGroups[CURRENT_COMPANY_STATUS].push({
          ...currentCompany,
          filterType: CURRENT_COMPANY_STATUS,
          iconUrl: ICONS_BY_COMPANY_TYPE[CURRENT_COMPANY_STATUS]
        })
      }

      return companyGroups
    },
    { activated: [], active: [], inProcess: [], inactive: [], testing: [] }
  )

export const validateFormControls = (
  values: Record<string, string | { value: string }>,
  controls: IFormProps
): Record<string, string> => {
  const errors: Record<string, string> = {}

  Object.entries(controls).forEach(([key]) => {
    const control = controls[key]
    const value = control.type === 'select' ? (values[key] as { value: string }).value : values[key]
    const error = composeValidators(...control.validations)(value, values)

    if (error) {
      errors[key] = error
    }
  })

  return errors
}

const getToastPropsFromError = (error: AxiosError<Record<string, string>>): IToastAlert => {
  const text = error.response?.data?.causes || error?.message || ''
  const title = error.response?.data?.message || ''

  return {
    text,
    title,
    type: 'error'
  }
}

const handleWarningAxiosRequest = (
  t: TFunction,
  response: AxiosResponse,
  onWarning?: (response: AxiosResponse) => void
): void => {
  onWarning ? onWarning(response) : ToastAlert(TOAST_PROPS_DEFAULT('warning', t))
  console.log(response)
}

const handleSuccessAxiosRequest = (
  t: TFunction,
  response: AxiosResponse,
  onSuccess?: (response: AxiosResponse) => void
): void => {
  onSuccess ? onSuccess(response) : ToastAlert(TOAST_PROPS_DEFAULT('success', t))
}

const handleErrorAxiosRequest = (
  t: TFunction,
  error: AxiosError<Record<string, string>>,
  onError?: (error: AxiosError) => void
): void => {
  const props =
  error.response?.status === HTTP_RESPONSE_STATUS_CODE['500']
    ? TOAST_PROPS_DEFAULT('error', t)
    : getToastPropsFromError(error)
  onError ? onError(error) : ToastAlert(props)
  console.log(error)
}

const handleLoadingAxiosRequest = (status: boolean, onLoading?: (loading: boolean) => void): void => {
  onLoading && onLoading(status)
}

export const commonWrapperRequest = (
  t: TFunction,
  responseStatus: string,
  request: Promise<AxiosResponse>,
  updateLoading?: (loading: boolean) => void,
  onSuccess?: (response: AxiosResponse) => void,
  onError?: (response: AxiosResponse) => void,
  onFail?: (error: AxiosError) => void,
  onStart?: () => void,
  onEnd?: () => void
): Promise<AxiosResponse> => {
  handleLoadingAxiosRequest(true, updateLoading)
  onStart && onStart()

  request
    .then(response => {
      const { status } = response

      if (status === HTTP_RESPONSE_STATUS_CODE[responseStatus]) {
        handleSuccessAxiosRequest(t, response, onSuccess)
      } else {
        handleWarningAxiosRequest(t, response, onError)
      }
    })
    .catch(error => {
      handleErrorAxiosRequest(t, error, onFail)
    })
    .finally(() => {
      handleLoadingAxiosRequest(false, updateLoading)
      onEnd && onEnd()
    })

  return request
}

export const axiosRequestHandler = ({
  onEnd,
  onError,
  onLoading,
  onStart,
  onSuccess,
  onWarning,
  request,
  successStatus = HTTP_RESPONSE_STATUS_CODE['200'],
  t
}: IAxiosRequestHandler): Promise<AxiosResponse> => {
  onStart && onStart()
  handleLoadingAxiosRequest(true, onLoading)

  request
    .then(response => {
      if (response.status === successStatus) {
        handleSuccessAxiosRequest(t, response, onSuccess)
      } else {
        handleWarningAxiosRequest(t, response, onWarning)
      }
    })
    .catch(error => {
      handleErrorAxiosRequest(t, error, onError)
    })
    .finally(() => {
      handleLoadingAxiosRequest(false, onLoading)
      onEnd && onEnd()
    })

  return request
}
